<script lang="ts">
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
} from '@inscribe/signal';
import { ref, onMounted, nextTick, onBeforeUnmount } from 'vue';

export enum ExpandableContainerSize {
  SMALL = 'small',
  MEDIUM = 'medium',
}

export default {
  name: 'ExpandableContainer',
  components: { iButton },
  props: {
    expandable: {
      type: Boolean,
      default: true,
    },
    startExpanded: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: undefined,
    },
    size: {
      type: String,
      default: ExpandableContainerSize.MEDIUM,
    },
  },
  setup(props) {
    let isExpanded = ref(props.startExpanded);
    let isFadeIn = ref(props.startExpanded);
    let height = ref(0);
    const contentElement = ref<HTMLElement | null>(null);
    let observer: MutationObserver | null = null;
    const toggleExpand = (evt: Event) => {
      if (props.expandable) {
        evt.stopPropagation();
        let newIsExpandedValue = !isExpanded.value;
        if (newIsExpandedValue) {
          // expanded, first expand the container, half way through, fade in the content
          isExpanded.value = newIsExpandedValue;
          setTimeout(() => {
            isFadeIn.value = newIsExpandedValue;
          }, 150 / 2);
        } else {
          // collapsed, first fade out the content, then collapse the container
          isFadeIn.value = newIsExpandedValue;
          setTimeout(() => {
            isExpanded.value = newIsExpandedValue;
          }, 150 / 2);
        }
      }
    };

    onMounted(() => {
      window.addEventListener('resize', setHeight);

      observer = new MutationObserver(
        function () {
          nextTick(() => {
            setHeight();
          });
        }.bind(this),
      );

      // Setup the observer
      observer.observe(contentElement.value, {
        attributes: true,
        childList: true,
        subtree: true,
      });

      nextTick(() => {
        setHeight();
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', setHeight);
    });

    const setHeight = () => {
      if (contentElement.value) {
        const newHeight = contentElement.value.children.length
          ? Array.from(contentElement.value.children).reduce((acc, child) => {
              return acc + (child as HTMLElement).scrollHeight;
            }, 0)
          : contentElement.value.scrollHeight;
        height.value = newHeight;
      }
    };

    onBeforeUnmount(() => {
      observer?.disconnect();
    });

    return {
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      isExpanded,
      isFadeIn,
      toggleExpand,
      height,
      contentElement,
      ExpandableContainerSize,
    };
  },
};
</script>

<template>
  <div
    :class="{
      expandable: expandable,
      'i-gap-2': isExpanded,
      small: size === ExpandableContainerSize.SMALL,
    }"
    class="expandable-container d-flex flex-column i-p-2"
  >
    <!-- header -->
    <div
      class="header d-flex align-items-center justify-content-between"
      :class="{
        'i-px-9 i-py-5': size === ExpandableContainerSize.MEDIUM,
        'i-px-6 i-py-4': size === ExpandableContainerSize.SMALL,
      }"
      @click="toggleExpand"
    >
      <div class="title-container">
        <div class="title i-text-button-2 i-text-neutral-120">{{ title }}</div>
        <div
          v-if="subtitle"
          :class="{
            'i-text-body-2': size === ExpandableContainerSize.MEDIUM,
            'i-text-button-2': size === ExpandableContainerSize.SMALL,
          }"
          class="subtitle i-text-neutral-110"
        >
          {{ subtitle }}
        </div>
      </div>
      <div class="right-tools d-flex align-items-center i-gap-6">
        <slot name="tools" />
        <i-button
          v-if="expandable"
          aria-label="Expandable container: Toggle section"
          data-dd-action-name="expandable-container-toggle"
          :left-icon="isExpanded ? 'medium-chevron-up' : 'medium-chevron-down'"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          @button-click="toggleExpand"
        />
      </div>
    </div>

    <!-- content -->
    <div
      :class="{ expanded: isExpanded, 'fade-in': isFadeIn }"
      :style="{ height: isExpanded ? `${height}px` : '0' }"
      ref="contentElement"
      class="content d-flex flex-column i-radius-3xl i-gap-1"
    >
      <slot name="items" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.expandable-container {
  background: $light-neutral-30;
  border-radius: 24px;

  &.small {
    .header {
      min-height: 32px;
    }
  }

  &.expandable {
    .header {
      cursor: pointer;
    }
  }

  .header {
    width: 100%;
    min-height: 48px;
  }

  .content {
    overflow: hidden;
    transition:
      height 150ms ease-in-out,
      opacity 150ms ease-in-out;
    transition-delay: opacity 1500ms;
    opacity: 0;

    &.fade-in {
      opacity: 1;
    }
  }
}
</style>
